<template>
    <div class="dashcam-provisioning">
        <div
            v-if="!isAdmin"
            class="access-denied-text"
        >
            {{ $t('subscription.dashcam_access_denied') }}
        </div>

        <template v-else>
            <h4 class="form-header">{{ $t(sectionTitleKey) }}</h4>

            <template v-if="isProvisioned">
                <span class="span-title">{{ $t('subscription.dash_cam_s3_bucket') }}</span>
                <span class="tip-light">{{ dashCam.s3_bucket }}</span>

                <span class="span-title">{{ $t('subscription.archiving_period_months') }}</span>
                <span class="tip-light">{{ dashCam.archiving_period_months ?? $t('subscription.not_set') }}</span>

                <span class="span-title">{{ $t('subscription.glacier_transition_month') }}</span>
                <span class="tip-light">{{ dashCam.glacier_transition_month ?? $t('subscription.not_set') }}</span>
            </template>

            <template v-else>
                <div class="info-box dashcam-info-box">
                    <font-awesome-icon
                        icon="info-circle"
                        class="info-circle"
                    />
                    <div class="dashcam-info-sections">
                        <div class="dashcam-info-section">
                            <strong>{{ $t('subscription.dashcam_activation_note_title') }}</strong>
                            <div>{{ $t('subscription.dashcam_provisioning_required_hint') }}</div>
                        </div>
                    </div>
                </div>

                <span class="span-title">{{ $t('subscription.archiving_period_months') }}</span>
                <input
                    v-model="archivingPeriodMonths"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': fieldErrors.archivingPeriodMonths }"
                />
                <div
                    v-if="fieldErrors.archivingPeriodMonths"
                    class="text-danger small"
                >
                    {{ fieldErrors.archivingPeriodMonths }}
                </div>

                <span class="span-title">{{ $t('subscription.glacier_transition_month') }}</span>
                <input
                    v-model="glacierTransitionMonth"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': fieldErrors.glacierTransitionMonth }"
                />
                <div
                    v-if="fieldErrors.glacierTransitionMonth"
                    class="text-danger small"
                >
                    {{ fieldErrors.glacierTransitionMonth }}
                </div>

                <div class="info-box dashcam-info-box">
                    <font-awesome-icon
                        icon="info-circle"
                        class="info-circle"
                    />
                    <div class="dashcam-info-sections">
                        <div class="dashcam-info-section">
                            <strong>{{ $t('subscription.archiving_period_months') }}</strong>
                            <div>{{ $t('subscription.dashcam_archiving_period_info') }}</div>
                        </div>

                        <div class="dashcam-info-section">
                            <strong>{{ $t('subscription.glacier_transition_month') }}</strong>
                            <div>{{ $t('subscription.dashcam_glacier_transition_info') }}</div>
                        </div>
                    </div>
                </div>

                <button
                    class="btn btn-success btn-sm form-button"
                    :disabled="loading"
                    @click="submit"
                >
                    {{ $t('subscription.dashcam_provision_button') }}
                </button>
                <div
                    v-if="loading"
                    id="loader"
                    class="spinner"
                />
            </template>
            <hr>
        </template>
    </div>
</template>

<script>
import {inject} from 'vue'
import {restApi} from '../mixins/RestApiMixin'

const PROVISIONING_ERROR_MAP = {
    '400|MISSING_REQUIRED_FIELD':          { key: 'subscription.dashcam_error_missing_required_field' },
    '400|INVALID_PERIOD_VALUE':            { key: 'subscription.dashcam_error_invalid_period_value' },
    '400|GLACIER_NOT_BEFORE_ARCHIVING':    { key: 'subscription.dashcam_error_glacier_not_before_archiving' },
    '400|COMPANY_NAME_NOT_SLUGGABLE':      { key: 'subscription.dashcam_error_company_name_not_sluggable' },
    '400|INVALID_BUCKET_NAME':             { useBackendMessage: true },
    '409|DUPLICATE_BUCKET':                { key: 'subscription.dashcam_error_duplicate_bucket', refetch: true },
    '409|DUPLICATE_ORGANIZATION':          { key: 'subscription.dashcam_error_duplicate_bucket', refetch: true },
    '502|BUCKET_CREATION_FAILED':                       { key: 'subscription.dashcam_error_transient' },
    '502|LIFECYCLE_CONFIGURATION_FAILED':               { key: 'subscription.dashcam_error_transient' },
    '502|EVENTBRIDGE_NOTIFICATION_ENABLEMENT_FAILED':   { key: 'subscription.dashcam_error_transient' },
    '502|REGISTRY_WRITE_FAILED':                        { key: 'subscription.dashcam_error_transient' },
    '502|LOGGING_FAILED':                               { key: 'subscription.dashcam_error_transient' }
}

const PROVISIONING_STATUS_FALLBACK = {
    404: { key: 'subscription.dashcam_error_unexpected' },
    500: { key: 'subscription.dashcam_error_server_config' },
    502: { key: 'subscription.dashcam_error_transient' }
}

// Pure, side-effect-free resolver: given a failed provisioning response's status and body,
// returns a resolution descriptor. Does not call $t() itself (no access to `this` at module
// scope) — the caller (handleFailure, task 7.2) turns the descriptor into a display string.
// Callers must handle status === 403 (access-denied) before invoking this function.
function resolveProvisioningError(status, body) {
    if (status === 500) {
        return PROVISIONING_STATUS_FALLBACK[500]
    }

    const mapped = PROVISIONING_ERROR_MAP[`${status}|${body?.reason}`]
    if (mapped) {
        return mapped
    }

    const statusFallback = PROVISIONING_STATUS_FALLBACK[status]
    if (statusFallback) {
        return statusFallback
    }

    if (!status) {
        // Network error / timeout — no HTTP response received at all (Requirement 5.10):
        // treated identically to the 502-without-reason fallback.
        return PROVISIONING_STATUS_FALLBACK[502]
    }

    // Undocumented (status, reason) combination — Requirement 6.2 fallback. The caller builds
    // the message from body.error via $t('subscription.dashcam_error_fallback', {error}), or
    // $t('subscription.dashcam_error_generic_fallback') when body.error is missing/empty.
    return { fallback: true }
}

export default {
    name: 'DashcamBucketProvisioning',
    mixins: [restApi],
    props: {
        companyId: {
            type: [Number, String],
            required: true
        },
        dashCam: {
            type: Object,
            required: true
        },
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    emits: ['provisioned', 'refetch-required'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            archivingPeriodMonths: '12',
            glacierTransitionMonth: '6',
            fieldErrors: {
                archivingPeriodMonths: null,
                glacierTransitionMonth: null
            },
            loading: false
        }
    },
    computed: {
        isProvisioned() {
            return this.dashCam?.s3_bucket != null
        },
        sectionTitleKey() {
            return this.isProvisioned
                ? 'subscription.dashcam_storage_section_title'
                : 'subscription.dashcam_provisioning_section_title'
        }
    },
    watch: {
        isAdmin(newValue) {
            if (!newValue) {
                this.loading = false
            }
        }
    },
    methods: {
        submit() {
            if (!this.validate()) return
            this.loading = true
            this.restAdd(
                this.dashcamProvisionBucketUrl(this.companyId),
                {
                    archiving_period_months: Number(this.archivingPeriodMonths),
                    glacier_transition_month: Number(this.glacierTransitionMonth)
                },
                (data) => {
                    this.loading = false
                    this.$emit('provisioned', data.dash_cam)
                },
                (errorResponse) => {
                    this.loading = false
                    this.handleFailure(errorResponse)
                }
            )
        },

        handleFailure(errorResponse) {
            if (errorResponse?.status === 403) {
                this.showAlert(this.$t('subscription.dashcam_access_denied'))
                return
            }
            const resolved = resolveProvisioningError(errorResponse?.status, errorResponse?.data)
            const message = resolved.useBackendMessage
                ? (errorResponse.data?.error || this.$t('subscription.dashcam_error_generic_fallback'))
                : (resolved.fallback
                    ? (errorResponse?.data?.error
                        ? this.$t('subscription.dashcam_error_fallback', {error: errorResponse.data.error})
                        : this.$t('subscription.dashcam_error_generic_fallback'))
                    : this.$t(resolved.key))
            this.showAlert(message)
            if (resolved.refetch) this.$emit('refetch-required')
        },

        validate() {
            this.fieldErrors = {archivingPeriodMonths: null, glacierTransitionMonth: null}
            const a = this.parsePositiveInteger(this.archivingPeriodMonths)
            const g = this.parsePositiveInteger(this.glacierTransitionMonth)
            if (a === null) this.fieldErrors.archivingPeriodMonths = this.$t('subscription.dashcam_validation_positive_integer')
            if (g === null) this.fieldErrors.glacierTransitionMonth = this.$t('subscription.dashcam_validation_positive_integer')
            if (a !== null && g !== null && g >= a) {
                this.fieldErrors.glacierTransitionMonth = this.$t('subscription.dashcam_validation_glacier_before_archiving')
            }
            return !this.fieldErrors.archivingPeriodMonths && !this.fieldErrors.glacierTransitionMonth
        },

        parsePositiveInteger(value) {
            const trimmed = String(value ?? '').trim()
            if (trimmed === '' || !/^\d+$/.test(trimmed)) return null
            const n = Number(trimmed)
            return (Number.isInteger(n) && n > 0) ? n : null
        }
    }
}
</script>

<style scoped>
.dashcam-info-box {
    align-items: flex-start;
    margin-bottom: 0.75rem;
    margin-top: 0.75rem;
}

.dashcam-info-sections {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.dashcam-info-section strong {
    display: block;
}
</style>
